import {apiClient} from '../apiClient';
import {mapProductionComponentsRedactionStyles} from '../utils/templateProductionComponentsMapper';
import type {
  Assignment,
  CreateProductionTemplatePayload,
  CreateProductionTemplateResponse,
  DeleteProductionResponse,
  DownloadProductionResponse,
  EditViewProductionBasicInfoResponse,
  GetNativeErrorListRequestPayload,
  GetNativeErrorListResponse,
  GetErrorListRequestPayload,
  GetErrorListResponse,
  ExportErrorListRequestParams,
  ExportErrorListResponse,
  GetProductLocationViewRequestParam,
  GetProductLocationViewResponse,
  ValidateDestinationRequestParams,
  ValidateDestinationResponse,
  CreateDestinationRequestBody,
  CreateDestinationResponse,
  SaveProductionLocationRequestBody,
  SaveProductionLocationResponse,
  GetSummaryViewRequestParam,
  GetSummaryViewResponse,
  SaveSummaryRequestBody,
  SaveSummaryResponse,
  GetGenerateViewRequestParam,
  GetGenerateViewResponse,
  GetGenerateStatusRequestParam,
  GetGenerateStatusResponse,
  GetExportBatesRangeRequestParam,
  GetExportBatesRangeResponse,
  ExecuteGenerateRequestBody,
  ExecuteGenerateResponse,
  ExecutePlaceholderingRequestBody,
  ExecutePlaceholderingResponse,
  ConfirmProductionRequestParams,
  ConfirmProductionResponse,
  GetShareableLinksRequestParams,
  GetShareableLinksResponse,
  GetConfirmViewRequestParam,
  GetConfirmViewResponse,
  GetProductionPreviewRequestParam,
  GetProductionPreviewResponse,
  GetProductionPreviewFileRequestParam,
  GetProductionPreviewFileResponse,
  GetDatComponentRequestParam,
  GetDatComponentResponse,
  GetTemplateDatComponentResponse,
  GetDocumentSelectionViewRequestParam,
  GetDocumentSelectionViewResponse,
  GetManageTemplatesRequestParam,
  GetManageTemplatesResponse,
  GetMp3FileComponentRequestParam,
  GetMp3FileComponentResponse,
  GetNativeComponentRequestParam,
  GetNativeComponentResponse,
  GetProductionBasicInfoRequestParam,
  GetProductionBasicInfoResponse,
  GetProductionComponentsRequestParam,
  GetProductionComponentsResponse,
  GetProductionGuardMatchResultsPayload,
  GetProductionGuardMatchResultsResponse,
  GetProductionGuardViewRequestParam,
  GetProductionGuardViewResponse,
  GetTemplateDetailsRequestParam,
  GetTemplateNumberingSortingDetailsResponse,
  GetNumberingSortingViewResponse,
  GetProductionSetInfoRequestParams,
  GetProductionSetInfoResponse,
  GetProductionTilesRequestParam,
  GetSecurityRes,
  GetTextComponentRequestParam,
  GetTextComponentResponse,
  GetTemplateTextComponentResponse,
  GetTiffComponentRequestParam,
  GetTiffComponentResponse,
  GetTranslationComponentRequestParam,
  GetTranslationComponentResponse,
  ICreateSetPayload,
  IDeleteSetPayload,
  IDeleteSetResponse,
  IGetProductionsResponse,
  ISecurityGroupDraftSaveRequest,
  IUpdateSetPayload,
  IUpdateSetResponse,
  NextBatesResponse,
  ProductionExportOptionsResponse,
  SaveDocumentsSelectionPayload,
  SaveDocumentsSelectionResponse,
  SaveProductionBasicInfoPayload,
  SaveProductionBasicInfoResponse,
  SaveProductionComponentsPayload,
  SaveProductionComponentsResponse,
  SaveProductionGuardViewPayload,
  SaveProductionGuardViewResponse,
  UploadSeedFileResponse,
  ValidateDownloadRequest,
  ValidateDownloadResponse,
  GetSeedPathRequestParam,
  GetSeedPathResponse,
  SeedPageCountRequest,
  SeedPageCountResponse,
  ValidateDownloadURIRequest,
  ValidateDownloadURIResponse,
  CancelProductionResponse,
  CancelProductionRequest,
} from '../types/production.type';
import {
  getReProductionGenerateStatusParams,
  GetReProductionGenerateStatusResponse,
  GetReProductionExportBatesRangeRequestParam,
  GetReProductionGenerateViewResponse,
  GetReProductionGenerateViewRequestParam,
  GetReProductionExecuteGenerateRequestBody,
  GetReProductionExecutePlaceholderingRequestBody,
} from '../types/reproduction.type';

import {
  IGetExportsResponse,
  GetExportsTilesRequestParam,
} from '../types/export.type';
import {
  BackendErrorData,
  parseApiErrorWithLocalization,
  parseApiErrorWithoutCode,
} from '../../utils/custom-errors/parseApiError';
import {AxiosError} from 'axios';
import BASE_URL from '../../config/env';

import {
  EditViewReProductionBasicInfoResponse,
  GetReProductionBasicInfoRequestParam,
  GetReProductionBasicInfoResponse,
  GetReProductLocationViewRequestParam,
  GetReProductLocationViewResponse,
  ReProductionNumberingRequestParam,
  ReProductionNumberingResponse,
  SaveReProductionBasicInfoPayload,
  SaveReProductionBasicInfoResponse,
  SaveReProductionLocationRequestBody,
  SaveReProductionLocationResponse,
  GetReProductionConfirmViewResponse,
  SaveReProductionNumberingRequestParam,
  SaveReProductionNumberingResponse,
  ReProductionDocumentsCountRequest,
  ReProductionDocumentsCountResponse,
} from '../types/reproduction.type';

import {
  GetPriorProductionListRequestParam,
  GetPriorProductionListResponse,
  SavePriorProductionToReProduceRequestParam,
  SavePriorProductionToReProduceResponse,
} from '../types/reproduction.type';
const productionEndpoint = (path: string) =>
  `${BASE_URL}/productions/api/v1/production${path}`;

const proviewEndpoint = (path: string) =>
  `${BASE_URL}/productions/api/v1/proview${path}`;

export const getManageTemplateList = async (
  params: GetManageTemplatesRequestParam
): Promise<GetManageTemplatesResponse> => {
  const {data} = await apiClient.get<GetManageTemplatesResponse>(
    productionEndpoint('/templates'),
    {params}
  );

  return data;
};

export const deleteManageTemplate = async (templateId: number) => {
  const {data} = await apiClient.delete(
    productionEndpoint(`/templates/delete/${templateId}`)
  );
  return data;
};

export const getProductions = async (
  params: GetProductionTilesRequestParam
): Promise<IGetProductionsResponse> => {
  const {data} = await apiClient.get<IGetProductionsResponse>(
    productionEndpoint(`/view/renderproductiontiles`),
    {params}
  );
  return data;
};

export const getReProductionNumbering = async (
  params: ReProductionNumberingRequestParam
): Promise<ReProductionNumberingResponse> => {
  const {data} = await apiClient.get<ReProductionNumberingResponse>(
    productionEndpoint(`/reproduction/numbering`),
    {params}
  );
  return data;
};

export const saveReProductionNumbering = async (
  payload: SaveReProductionNumberingRequestParam
): Promise<SaveReProductionNumberingResponse> => {
  try {
    const {data} = await apiClient.post<SaveReProductionNumberingResponse>(
      productionEndpoint('/reproduction/save-numbering'),
      payload
    );
    return data;
  } catch (error) {
    throw parseApiErrorWithoutCode(error as AxiosError<BackendErrorData>);
  }
};

export const getPriorProductionsList = async (
  payload: GetPriorProductionListRequestParam
): Promise<GetPriorProductionListResponse> => {
  try {
    const {data} = await apiClient.post<GetPriorProductionListResponse>(
      productionEndpoint(`/reproduction/select-productions`),
      payload
    );
    return data;
  } catch (error) {
    throw parseApiErrorWithoutCode(error as AxiosError<BackendErrorData>);
  }
};

export const savePriorProductionToReProduce = async (
  payload: SavePriorProductionToReProduceRequestParam
): Promise<SavePriorProductionToReProduceResponse> => {
  try {
    const {data} = await apiClient.post<SavePriorProductionToReProduceResponse>(
      productionEndpoint('/reproduction/save-prior-production'),
      payload
    );
    return data;
  } catch (error) {
    throw parseApiErrorWithLocalization(
      error as AxiosError<BackendErrorData>,
      'ReProductions'
    );
  }
};

export const getExports = async (
  params: GetExportsTilesRequestParam
): Promise<IGetExportsResponse> => {
  const {data} = await apiClient.get<IGetExportsResponse>(
    productionEndpoint(`/view/renderexporttiles`),
    {params}
  );
  return data;
};

export const securityGroupDefultSaveTemplate = async (
  payload: ISecurityGroupDraftSaveRequest
) => {
  const {data} = await apiClient.post(
    productionEndpoint(`/templates/default`),
    payload
  );
  return data;
};

export const getSecurityGroup = async (): Promise<GetSecurityRes> => {
  const {data} = await apiClient.get<GetSecurityRes>(
    productionEndpoint(`/view/home`)
  );
  return data;
};

export const getProductionsAndExportsOptions = async (
  selectedSecurityGrpId: number
): Promise<ProductionExportOptionsResponse> => {
  const {data} = await apiClient.get<ProductionExportOptionsResponse>(
    productionEndpoint(
      `/view/renderproductiontabs?securityGrpId=${selectedSecurityGrpId}`
    )
  );
  return data;
};

export const getProductionSetInfo = async (
  params: GetProductionSetInfoRequestParams
): Promise<GetProductionSetInfoResponse> => {
  const {data} = await apiClient.get<GetProductionSetInfoResponse>(
    productionEndpoint(`/view/renderproductionsetinfo`),
    {params}
  );
  return data;
};

export const createSet = async (payload: ICreateSetPayload) => {
  try {
    const {data} = await apiClient.post(productionEndpoint(`/sets`), payload);
    return data;
  } catch (error) {
    throw parseApiErrorWithLocalization(
      error as AxiosError<BackendErrorData>,
      'ProductionAndExports'
    );
  }
};

export const updateSet = async (
  payload: IUpdateSetPayload
): Promise<IUpdateSetResponse> => {
  try {
    const {productionSetId, ...rest} = payload;
    const requestPayload = {
      ...rest,
      ProductionSetId: productionSetId,
    };
    const {data} = await apiClient.put<IUpdateSetResponse>(
      productionEndpoint(`/sets/update`),
      requestPayload
    );
    return data;
  } catch (error) {
    throw parseApiErrorWithLocalization(
      error as AxiosError<BackendErrorData>,
      'ProductionAndExports'
    );
  }
};

export const getProductionBasicInfo = async (
  params: GetProductionBasicInfoRequestParam
): Promise<GetProductionBasicInfoResponse> => {
  const {data} = await apiClient.get<GetProductionBasicInfoResponse>(
    productionEndpoint('/view/basicinformation'),
    {params}
  );

  return data;
};

export const saveProductionBasicInfo = async (
  payload: SaveProductionBasicInfoPayload
): Promise<SaveProductionBasicInfoResponse> => {
  try {
    const {data} = await apiClient.post<SaveProductionBasicInfoResponse>(
      productionEndpoint('/basic-info'),
      payload
    );
    return data;
  } catch (error) {
    throw parseApiErrorWithLocalization(
      error as AxiosError<BackendErrorData>,
      'ProductionAndExports'
    );
  }
};

export const getReProductionBasicInfo = async (
  params: GetReProductionBasicInfoRequestParam
): Promise<GetReProductionBasicInfoResponse> => {
  const {reproductionId, ...rest} = params;
  const {data} = await apiClient.get<GetReProductionBasicInfoResponse>(
    productionEndpoint('/reproduction/view/basic-info'),
    {
      params: {
        ...rest,
        reproductionId,
      },
    }
  );
  return data;
};

export const saveReProductionBasicInfo = async (
  payload: SaveReProductionBasicInfoPayload
): Promise<SaveReProductionBasicInfoResponse> => {
  try {
    const {data} = await apiClient.post<SaveReProductionBasicInfoResponse>(
      productionEndpoint('/reproduction/basic-info'),
      payload
    );
    return data;
  } catch (error) {
    throw parseApiErrorWithLocalization(
      error as AxiosError<BackendErrorData>,
      'ReProductions'
    );
  }
};

export const getProductionBasicInfoInEditMode = async (
  productionId: string
): Promise<EditViewProductionBasicInfoResponse> => {
  const {data} = await apiClient.post(
    productionEndpoint(`/view/edit/${productionId}`)
  );
  return data;
};

export const getReProductionBasicInfoInEditMode = async (
  reproductionId: string
): Promise<EditViewReProductionBasicInfoResponse> => {
  const {data} = await apiClient.post(
    productionEndpoint(`/reproduction/edit/${reproductionId}`)
  );
  return data;
};

export const getDatComponent = async (
  params: GetDatComponentRequestParam
): Promise<GetDatComponentResponse> => {
  const {data} = await apiClient.get<GetDatComponentResponse>(
    productionEndpoint('/view/datcomponent'),
    {params}
  );
  return data;
};

export const getNativeComponent = async (
  params: GetNativeComponentRequestParam
): Promise<GetNativeComponentResponse> => {
  const {data} = await apiClient.get<GetNativeComponentResponse>(
    productionEndpoint('/view/nativecomponent'),
    {params}
  );
  return data;
};

export const getTextComponent = async (
  params: GetTextComponentRequestParam
): Promise<GetTextComponentResponse> => {
  const {data} = await apiClient.get<GetTextComponentResponse>(
    productionEndpoint('/view/textcomponent'),
    {params}
  );
  return data;
};

export const getMp3FileComponent = async (
  params: GetMp3FileComponentRequestParam
): Promise<GetMp3FileComponentResponse> => {
  const {data} = await apiClient.get<GetMp3FileComponentResponse>(
    productionEndpoint('/view/mp3filecomponent'),
    {params}
  );
  return data;
};

export const getTranslationComponent = async (
  params: GetTranslationComponentRequestParam
): Promise<GetTranslationComponentResponse> => {
  const {data} = await apiClient.get<GetTranslationComponentResponse>(
    productionEndpoint('/view/translationcomponent'),
    {params}
  );
  return data;
};

export const getTiffComponent = async (
  params: GetTiffComponentRequestParam
): Promise<GetTiffComponentResponse> => {
  const {data} = await apiClient.get<GetTiffComponentResponse>(
    productionEndpoint('/view/tiffcomponent'),
    {params}
  );
  return data;
};

export const getTemplateTiffComponent = async (
  params: GetTemplateDetailsRequestParam
): Promise<GetTiffComponentResponse> => {
  const {data} = await apiClient.get<GetTiffComponentResponse>(
    productionEndpoint('/templates/tiff'),
    {params}
  );
  return data;
};

export const getTemplateDatComponent = async (
  params: GetTemplateDetailsRequestParam
): Promise<GetTemplateDatComponentResponse> => {
  const {data} = await apiClient.get<GetTemplateDatComponentResponse>(
    productionEndpoint('/templates/dat'),
    {params}
  );
  return data;
};

export const getTemplateNativeComponent = async (
  params: GetTemplateDetailsRequestParam
): Promise<GetNativeComponentResponse> => {
  const {data} = await apiClient.get<GetNativeComponentResponse>(
    productionEndpoint('/templates/native'),
    {params}
  );
  return data;
};

export const getTemplateTranslationComponent = async (
  params: GetTemplateDetailsRequestParam
): Promise<GetTranslationComponentResponse> => {
  const {data} = await apiClient.get<GetTranslationComponentResponse>(
    productionEndpoint('/templates/translation'),
    {params}
  );
  return data;
};

export const getTemplateMp3FileComponent = async (
  params: GetTemplateDetailsRequestParam
): Promise<GetMp3FileComponentResponse> => {
  const {data} = await apiClient.get<GetMp3FileComponentResponse>(
    productionEndpoint('/templates/mp3'),
    {params}
  );
  return data;
};

export const getTemplateTextComponent = async (
  params: GetTemplateDetailsRequestParam
): Promise<GetTemplateTextComponentResponse> => {
  const {data} = await apiClient.get<GetTemplateTextComponentResponse>(
    productionEndpoint('/templates/text'),
    {params}
  );
  return data;
};

export const getProductionComponents = async (
  params: GetProductionComponentsRequestParam
): Promise<GetProductionComponentsResponse> => {
  const {data} = await apiClient.get(
    productionEndpoint('/view/productioncomponents'),
    {params}
  );
  return mapProductionComponentsRedactionStyles(data);
};

export const getTemplateComponents = async (
  params: GetTemplateDetailsRequestParam
): Promise<GetProductionComponentsResponse> => {
  const {data} = await apiClient.get(
    productionEndpoint('/templates/components-details'),
    {
      params: {
        TemplateId: params.templateId,
        SecurityGroupIdForProduction: params.securityGroupId,
      },
    }
  );
  return mapProductionComponentsRedactionStyles(data);
};

export const saveProductionComponents = async (
  payload: SaveProductionComponentsPayload
): Promise<SaveProductionComponentsResponse> => {
  const {data} = await apiClient.post<SaveProductionComponentsResponse>(
    productionEndpoint('/components'),
    payload
  );
  return data;
};

export const createProductionTemplate = async (
  payload: CreateProductionTemplatePayload
): Promise<CreateProductionTemplateResponse> => {
  try {
    const {data} = await apiClient.post<CreateProductionTemplateResponse>(
      productionEndpoint('/templates'),
      payload
    );
    return data;
  } catch (error) {
    throw parseApiErrorWithLocalization(
      error as AxiosError<BackendErrorData>,
      'ProductionAndExports'
    );
  }
};

export const deleteProduction = async (
  productionId: string
): Promise<DeleteProductionResponse> => {
  const {data} = await apiClient.delete<DeleteProductionResponse>(
    productionEndpoint(`/delete/${productionId}`)
  );
  return data;
};

export const cancelProduction = async (
  cancelRequest: CancelProductionRequest
): Promise<CancelProductionResponse> => {
  try {
    const {data} = await apiClient.post<CancelProductionResponse>(
      productionEndpoint(`/cancel`),
      cancelRequest
    );
    return data;
  } catch (error) {
    throw parseApiErrorWithLocalization(
      error as AxiosError<BackendErrorData>,
      'ProductionAndExports'
    );
  }
};

export const retryCancelProduction = async (
  cancelRequest: CancelProductionRequest
): Promise<CancelProductionResponse> => {
  try {
    const {data} = await apiClient.post<CancelProductionResponse>(
      productionEndpoint(`/retry-cancel`),
      cancelRequest
    );
    return data;
  } catch (error) {
    throw parseApiErrorWithLocalization(
      error as AxiosError<BackendErrorData>,
      'ProductionAndExports'
    );
  }
};

export const deleteSet = async (
  payload: IDeleteSetPayload
): Promise<IDeleteSetResponse> => {
  try {
    const {data} = await apiClient.delete<IDeleteSetResponse>(
      productionEndpoint(`/sets/delete`),
      {data: payload}
    );
    return data;
  } catch (error) {
    throw parseApiErrorWithLocalization(
      error as AxiosError<BackendErrorData>,
      'ProductionAndExports'
    );
  }
};

export const uploadFile = async (file: File): Promise<{message: string}> => {
  const formData = new FormData();
  formData.append('file', file);

  const {data} = await apiClient.post<{message: string}>(
    productionEndpoint('/upload/file'),
    formData,
    {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    }
  );
  return data;
};

export const uploadSeedFile = async (
  file: File
): Promise<UploadSeedFileResponse> => {
  const formData = new FormData();
  formData.append('file', file);

  const {data} = await apiClient.post<UploadSeedFileResponse>(
    productionEndpoint('/upload/seed-file'),
    formData,
    {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    }
  );
  return data;
};

export const getValidateDownloadURI = async (
  params: ValidateDownloadURIRequest
): Promise<ValidateDownloadURIResponse> => {
  try {
    const endpoint = params.absoluteUri?.includes('/prod/')
      ? productionEndpoint('/download/archive')
      : productionEndpoint('/download/dat');
    const {data} = await apiClient.get<ValidateDownloadURIResponse>(endpoint, {
      params: {
        key: params.key,
        urlHost: params.urlHost,
        absoluteUri: params.absoluteUri,
        ipAddress: params.ipAddress,
      },
    });
    return data;
  } catch (error) {
    throw parseApiErrorWithoutCode(error as AxiosError<BackendErrorData>);
  }
};

export const validateDownload = async (
  payload: ValidateDownloadRequest,
  queryParam?: string
): Promise<ValidateDownloadResponse> => {
  const response = await apiClient.post(
    productionEndpoint('/download/validate-download'),
    payload,
    {
      params: queryParam ? {[queryParam]: ''} : {},
      responseType: 'blob',
      validateStatus: () => true,
    }
  );

  const contentType = response.headers['content-type'] || '';

  // If backend returned JSON error (even though status is 4xx/5xx)
  if (contentType.includes('application/json')) {
    const text = await response.data.text();
    let apiError: BackendErrorData;
    try {
      apiError = JSON.parse(text);
    } catch {
      apiError = {
        title: 'Unknown error',
        status: response.status,
        detail: text,
        instance: response.config.url || '',
      };
    }

    const error = new Error(apiError.detail?.split('\n')[0] || apiError.title);
    error.message = apiError.detail?.split('\n')[0] ?? '';
    throw error;
  }
  // Extract filename from content-disposition header
  const contentDisposition = response.headers['content-disposition'];
  let filename = 'download';
  if (contentDisposition) {
    const filenameMatch = contentDisposition.match(
      /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/
    );
    if (filenameMatch && filenameMatch[1]) {
      filename = filenameMatch[1].replace(/['"]/g, '');
      // Handle UTF-8 encoded filename
      if (filename.startsWith("UTF-8''")) {
        filename = decodeURIComponent(filename.replace("UTF-8''", ''));
      }
    }
  }

  return {
    blob: response.data,
    filename,
    contentType: response.headers['content-type'] || 'application/octet-stream',
  };
};

export const getSeedPath = async (
  params: GetSeedPathRequestParam
): Promise<GetSeedPathResponse> => {
  const {data} = await apiClient.get<GetSeedPathResponse>(
    productionEndpoint('/seed-path'),
    {
      params: {
        documentId: params.documentId,
        productionId: params.productionId,
      },
    }
  );
  return data;
};

export const seedPageCount = async (
  payload: SeedPageCountRequest,
  queryParam?: string
): Promise<SeedPageCountResponse> => {
  const {data} = await apiClient.post<SeedPageCountResponse>(
    productionEndpoint('/seed-page-count'),
    payload,
    {
      params: queryParam ? {[queryParam]: ''} : {},
    }
  );
  return data;
};

export const uploadReProductionSeedFile = async (
  file: File
): Promise<UploadSeedFileResponse> => {
  const formData = new FormData();
  formData.append('file', file);

  try {
    const {data} = await apiClient.post<UploadSeedFileResponse>(
      productionEndpoint('/reproduction/upload/seed-file'),
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );
    return data;
  } catch (error) {
    throw parseApiErrorWithLocalization(
      error as AxiosError<BackendErrorData>,
      'ReProductions'
    );
  }
};

export const getReProductionSeedPath = async (
  params: GetSeedPathRequestParam
): Promise<GetSeedPathResponse> => {
  try {
    const {data} = await apiClient.get<GetSeedPathResponse>(
      productionEndpoint('/reproduction/seed-path'),
      {
        params: {
          documentId: params.documentId,
          productionId: params.productionId,
        },
      }
    );
    return data;
  } catch (error) {
    throw parseApiErrorWithLocalization(
      error as AxiosError<BackendErrorData>,
      'ReProductions'
    );
  }
};

export const reProductionSeedPageCount = async (
  payload: SeedPageCountRequest,
  queryParam?: string
): Promise<SeedPageCountResponse> => {
  try {
    const {data} = await apiClient.post<SeedPageCountResponse>(
      productionEndpoint('/reproduction/seed-page-count'),
      payload,
      {
        params: queryParam ? {[queryParam]: ''} : {},
      }
    );
    return data;
  } catch (error) {
    throw parseApiErrorWithLocalization(
      error as AxiosError<BackendErrorData>,
      'ReProductions'
    );
  }
};

export const getNumberingSortingView = async (params: {
  SecurityGroupId: number;
  productionId: number;
}): Promise<GetNumberingSortingViewResponse> => {
  const {data} = await apiClient.get<GetNumberingSortingViewResponse>(
    productionEndpoint('/view/numberingsortingview'),
    {params}
  );
  return data;
};

export const getTemplateNumberingSortingDetails = async (
  params: GetTemplateDetailsRequestParam
): Promise<GetTemplateNumberingSortingDetailsResponse> => {
  const {data} =
    await apiClient.get<GetTemplateNumberingSortingDetailsResponse>(
      productionEndpoint('/templates/numbering-sorting-details'),
      {
        params: {
          TemplateId: params.templateId,
          SecurityGroupIdForProduction: params.securityGroupId,
        },
      }
    );
  return data;
};

export const saveNumberingSorting = async (payload: {
  productionnumberingSettings: any;
  productionSortingSettings: any;
  isMarkComplete: boolean;
  productionId: number;
  productionSecurityGroupId: number;
}): Promise<{message: string}> => {
  const {data} = await apiClient.post<{message: string}>(
    productionEndpoint('/numbering-sorting'),
    payload
  );
  return data;
};

export const getBatesNext = async (params: {
  SecurityGroupId: number;
  productionId: number;
}): Promise<NextBatesResponse> => {
  const {data} = await apiClient.get(productionEndpoint('/bates/next'), {
    params,
  });

  return data;
};

export const getAssignments = async (
  selectedSecurityGrpId: number
): Promise<Assignment[]> => {
  const {data} = await apiClient.get<Assignment[]>(
    proviewEndpoint(`/tree/assignment?securityGrpId=${selectedSecurityGrpId}`)
  );
  return data;
};

export const getDocumentSelectionView = async (
  params: GetDocumentSelectionViewRequestParam
): Promise<GetDocumentSelectionViewResponse> => {
  const {data} = await apiClient.get<GetDocumentSelectionViewResponse>(
    productionEndpoint('/view/documentselectionview'),
    {params}
  );
  return data;
};

export const saveDocumentsSelection = async (
  payload: SaveDocumentsSelectionPayload
): Promise<SaveDocumentsSelectionResponse> => {
  const {data} = await apiClient.post<SaveDocumentsSelectionResponse>(
    productionEndpoint('/documents-selection'),
    payload
  );
  return data;
};

export const getReProductionDocumentsCount = async (
  payload: ReProductionDocumentsCountRequest
): Promise<ReProductionDocumentsCountResponse> => {
  const {data} = await apiClient.post<ReProductionDocumentsCountResponse>(
    productionEndpoint('/reproduction/documentscount'),
    payload
  );

  return data;
};

export const getProductionGuardView = async (
  params: GetProductionGuardViewRequestParam
): Promise<GetProductionGuardViewResponse> => {
  const {data} = await apiClient.get<GetProductionGuardViewResponse>(
    productionEndpoint('/view/productionguardview'),
    {params}
  );
  return data;
};

export const getTemplateGuardDetails = async (
  params: GetTemplateDetailsRequestParam
): Promise<GetProductionGuardViewResponse> => {
  const {data} = await apiClient.get<GetProductionGuardViewResponse>(
    productionEndpoint('/templates/guard-details'),
    {
      params: {
        TemplateId: params.templateId,
        SecurityGroupIdForProduction: params.securityGroupId,
      },
    }
  );
  return data;
};

export const saveProductionGuardView = async (
  payload: SaveProductionGuardViewPayload
): Promise<SaveProductionGuardViewResponse> => {
  const {data} = await apiClient.post<SaveProductionGuardViewResponse>(
    productionEndpoint('/guard'),
    payload
  );
  return data;
};

export const getProductionGuardMatchResults = async (
  payload: GetProductionGuardMatchResultsPayload
): Promise<GetProductionGuardMatchResultsResponse> => {
  const {data} = await apiClient.post<GetProductionGuardMatchResultsResponse>(
    productionEndpoint('/guard/match-results'),
    payload
  );
  return data;
};

export const downloadProduction = async (params: {
  sourcePath: string;
  fileName: string;
}): Promise<DownloadProductionResponse> => {
  const response = await apiClient.get(productionEndpoint('/downloads'), {
    params: {
      sourcePath: params.sourcePath,
      fileName: params.fileName,
    },
    responseType: 'blob',
  });

  // Extract filename from content-disposition header
  const contentDisposition = response.headers['content-disposition'];
  let filename = params.fileName;
  if (contentDisposition) {
    const filenameMatch = contentDisposition.match(
      /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/
    );
    if (filenameMatch && filenameMatch[1]) {
      filename = filenameMatch[1].replace(/['"]/g, '');
      // Handle UTF-8 encoded filename
      if (filename.startsWith("UTF-8''")) {
        filename = decodeURIComponent(filename.replace("UTF-8''", ''));
      }
    }
  }

  return {
    blob: response.data,
    filename,
    contentType: response.headers['content-type'] || 'application/zip',
  };
};

export const getNativeErrorList = async (
  payload: GetNativeErrorListRequestPayload
): Promise<GetNativeErrorListResponse> => {
  const {data} = await apiClient.post<GetNativeErrorListResponse>(
    productionEndpoint('/errors/native-list'),
    payload
  );
  return data;
};

export const getErrorList = async (
  payload: GetErrorListRequestPayload
): Promise<GetErrorListResponse> => {
  const {data} = await apiClient.post<GetErrorListResponse>(
    productionEndpoint('/errors/list'),
    payload
  );
  return data;
};

export const exportErrorList = async (
  params: ExportErrorListRequestParams
): Promise<ExportErrorListResponse> => {
  const {data} = await apiClient.get<ExportErrorListResponse>(
    productionEndpoint('/errors/export'),
    {
      params: {
        productionId: params.productionId,
        SecurityGroupID: params.SecurityGroupID,
        ModuleType: params.ModuleType,
      },
    }
  );
  return data;
};

export const getReProductionErrorList = async (
  payload: GetErrorListRequestPayload
): Promise<GetErrorListResponse> => {
  try {
    const {data} = await apiClient.post<GetErrorListResponse>(
      productionEndpoint('/reproduction/errors/list'),
      payload
    );
    return data;
  } catch (error) {
    throw parseApiErrorWithLocalization(
      error as AxiosError<BackendErrorData>,
      'ReProductions'
    );
  }
};

export const exportReProductionErrorList = async (
  params: ExportErrorListRequestParams
): Promise<ExportErrorListResponse> => {
  try {
    const {data} = await apiClient.get<ExportErrorListResponse>(
      productionEndpoint('/reproduction/errors/export'),
      {
        params: {
          productionId: params.productionId,
          SecurityGroupID: params.SecurityGroupID,
          ModuleType: params.ModuleType,
        },
      }
    );
    return data;
  } catch (error) {
    throw parseApiErrorWithLocalization(
      error as AxiosError<BackendErrorData>,
      'ReProductions'
    );
  }
};

export const getProductLocationView = async (
  params: GetProductLocationViewRequestParam
): Promise<GetProductLocationViewResponse> => {
  const {data} = await apiClient.get<GetProductLocationViewResponse>(
    productionEndpoint('/view/productlocationview'),
    {params}
  );
  return data;
};

export const validateDestination = async (
  params: ValidateDestinationRequestParams
): Promise<ValidateDestinationResponse> => {
  try {
    const {data} = await apiClient.get<ValidateDestinationResponse>(
      productionEndpoint('/destination/validate'),
      {
        params: {
          rootPath: params.rootPath,
        },
      }
    );
    return data;
  } catch (error) {
    throw parseApiErrorWithLocalization(
      error as AxiosError<BackendErrorData>,
      'ProductionAndExports'
    );
  }
};

export const createDestination = async (
  body: CreateDestinationRequestBody
): Promise<CreateDestinationResponse> => {
  try {
    const {data} = await apiClient.post<CreateDestinationResponse>(
      productionEndpoint('/destination'),
      body
    );
    return data;
  } catch (error) {
    throw parseApiErrorWithLocalization(
      error as AxiosError<BackendErrorData>,
      'ProductionAndExports'
    );
  }
};

export const saveProductionLocation = async (
  body: SaveProductionLocationRequestBody
): Promise<SaveProductionLocationResponse> => {
  try {
    const {data} = await apiClient.post<SaveProductionLocationResponse>(
      productionEndpoint('/location'),
      body
    );
    return data;
  } catch (error) {
    throw parseApiErrorWithLocalization(
      error as AxiosError<BackendErrorData>,
      'ProductionAndExports'
    );
  }
};

export const getSummaryView = async (
  params: GetSummaryViewRequestParam
): Promise<GetSummaryViewResponse> => {
  const {data} = await apiClient.get<GetSummaryViewResponse>(
    productionEndpoint('/view/summaryview'),
    {params}
  );
  return data;
};

export const saveSummary = async (
  body: SaveSummaryRequestBody
): Promise<SaveSummaryResponse> => {
  const {data} = await apiClient.post<SaveSummaryResponse>(
    productionEndpoint('/summary'),
    body
  );
  return data;
};

export const getGenerateView = async (
  params: GetGenerateViewRequestParam
): Promise<GetGenerateViewResponse> => {
  const {data} = await apiClient.get<GetGenerateViewResponse>(
    productionEndpoint('/view/generateview'),
    {params}
  );
  return data;
};

export const getReProductionDocumentSelectionView = async (
  params: GetDocumentSelectionViewRequestParam
): Promise<GetDocumentSelectionViewResponse> => {
  const {data} = await apiClient.get<
    GetDocumentSelectionViewResponse & {
      reProductionDocumentsSelection?: GetDocumentSelectionViewResponse['productionDocumentsSelection'];
    }
  >(productionEndpoint('/reproduction/documentview'), {params});

  const selection =
    data.productionDocumentsSelection ?? data.reProductionDocumentsSelection;

  return {
    ...data,
    productionDocumentsSelection:
      selection as GetDocumentSelectionViewResponse['productionDocumentsSelection'],
  };
};
export const getReProductionGenerateView = async (
  params: GetReProductionGenerateViewRequestParam
): Promise<GetReProductionGenerateViewResponse> => {
  const {data} = await apiClient.get<GetReProductionGenerateViewResponse>(
    productionEndpoint('/reproduction/generateview'),
    {params}
  );
  return data;
};

export const SaveReProductionDocumentSelection = async (
  payload: SaveDocumentsSelectionPayload
): Promise<SaveDocumentsSelectionResponse> => {
  try {
    const {data} = await apiClient.post<SaveDocumentsSelectionResponse>(
      productionEndpoint('/reproduction/saveselection'),
      payload
    );
    return data;
  } catch (error) {
    throw parseApiErrorWithLocalization(
      error as AxiosError<BackendErrorData>,
      'ReProductions'
    );
  }
};

export const getGenerateStatus = async (
  params: GetGenerateStatusRequestParam
): Promise<GetGenerateStatusResponse> => {
  const {data} = await apiClient.get<GetGenerateStatusResponse>(
    productionEndpoint('/generate/status'),
    {params}
  );
  return data;
};

export const getReProductionGenerateStatus = async (
  params: getReProductionGenerateStatusParams
): Promise<GetReProductionGenerateStatusResponse> => {
  const {data} = await apiClient.get<GetReProductionGenerateStatusResponse>(
    productionEndpoint('/reproduction/generatestatus'),
    {params}
  );
  return data;
};

export const getExportBatesRange = async (
  params: GetExportBatesRangeRequestParam
): Promise<GetExportBatesRangeResponse> => {
  const {data} = await apiClient.get<GetExportBatesRangeResponse>(
    productionEndpoint('/export/bates-range'),
    {
      params: {
        productionId: params.productionId,
      },
    }
  );
  return data;
};

export const getReProductionExportBatesRange = async (
  params: GetReProductionExportBatesRangeRequestParam
): Promise<GetExportBatesRangeResponse> => {
  const {data} = await apiClient.get<GetExportBatesRangeResponse>(
    productionEndpoint('/reproduction/export-bates-range'),
    {
      params,
      timeout: 60000,
    }
  );
  return data;
};

export const executeGenerate = async (
  body: ExecuteGenerateRequestBody
): Promise<ExecuteGenerateResponse> => {
  const {data} = await apiClient.post<ExecuteGenerateResponse>(
    productionEndpoint('/generate/execute'),
    body
  );
  return data;
};

export const reProductionExecuteGenerate = async (
  body: GetReProductionExecuteGenerateRequestBody
): Promise<ExecuteGenerateResponse> => {
  try {
    const {data} = await apiClient.post<ExecuteGenerateResponse>(
      productionEndpoint('/reproduction/generate-execute'),
      body
    );
    return data;
  } catch (error) {
    throw parseApiErrorWithLocalization(
      error as AxiosError<BackendErrorData>,
      'ReProductions'
    );
  }
};

export const executePlaceholdering = async (
  body: ExecutePlaceholderingRequestBody
): Promise<ExecutePlaceholderingResponse> => {
  const {data} = await apiClient.post<ExecutePlaceholderingResponse>(
    productionEndpoint('/generate/placeholdering'),
    body
  );
  return data;
};

export const reProductionExecutePlaceholdering = async (
  body: GetReProductionExecutePlaceholderingRequestBody
): Promise<ExecutePlaceholderingResponse> => {
  const {data} = await apiClient.post<ExecutePlaceholderingResponse>(
    productionEndpoint('/reproduction/placeholdering'),
    body
  );
  return data;
};

export const confirmProduction = async (
  params: ConfirmProductionRequestParams
): Promise<ConfirmProductionResponse> => {
  try {
    const {data} = await apiClient.request<ConfirmProductionResponse>({
      method: 'GET',
      url: productionEndpoint('/confirm'),
      params: {
        ProductionId: params.ProductionId,
        SecurityGroupId: params.SecurityGroupId,
        ProductionLabel: params.ProductionLabel,
        IsCommit: params.IsCommit,
      },
    });
    return data;
  } catch (error) {
    throw parseApiErrorWithoutCode(error as AxiosError<BackendErrorData>);
  }
};

export const getShareableLinks = async (
  params: GetShareableLinksRequestParams
): Promise<GetShareableLinksResponse> => {
  const {data} = await apiClient.request<GetShareableLinksResponse>({
    method: 'GET',
    url: productionEndpoint('/shareable-links'),
    params: {
      productionId: params.productionId,
      expiryDate: params.expiryDate,
      isRegenerate: params.isRegenerate,
    },
  });
  return data;
};

export const getConfirmView = async (
  params: GetConfirmViewRequestParam
): Promise<GetConfirmViewResponse> => {
  const {data} = await apiClient.get<GetConfirmViewResponse>(
    productionEndpoint('/confirm-view'),
    {
      params: {
        productionId: params.productionId,
        securityGroupId: params.securityGroupId,
        ModuleType: params.ModuleType,
      },
    }
  );
  return data;
};

export const getProductionPreview = async (
  params: GetProductionPreviewRequestParam
): Promise<GetProductionPreviewResponse> => {
  const {data} = await apiClient.get<GetProductionPreviewResponse>(
    productionEndpoint(`/preview/${params.productionId}`),
    {
      params: {},
    }
  );
  return data;
};

export const getProductionPreviewFile = async (
  params: GetProductionPreviewFileRequestParam
): Promise<GetProductionPreviewFileResponse> => {
  const response = await apiClient.get(productionEndpoint('/preview/file'), {
    params: {
      filePath: params.filePath,
    },
    responseType: 'blob',
  });

  // Extract filename from content-disposition header
  const contentDisposition = response.headers['content-disposition'];
  let filename = params.filePath.split('\\').pop() || params.filePath;
  if (contentDisposition) {
    const filenameMatch = contentDisposition.match(
      /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/
    );
    if (filenameMatch && filenameMatch[1]) {
      filename = filenameMatch[1].replace(/['"]/g, '');
      // Handle UTF-8 encoded filename
      if (filename.startsWith("UTF-8''")) {
        filename = decodeURIComponent(filename.replace("UTF-8''", ''));
      }
    }
  }

  return {
    blob: response.data,
    filename,
    contentType: response.headers['content-type'] || 'application/octet-stream',
  };
};

export const getProductionBatesRangeReport = async (
  sourceID: string
): Promise<Blob> => {
  const {data} = await apiClient.get(
    productionEndpoint('/export/bates-range/download'),
    {
      params: {sourceID},
      responseType: 'blob',
    }
  );
  return data;
};

export const getReProductLocationView = async (
  params: GetReProductLocationViewRequestParam
): Promise<GetReProductLocationViewResponse> => {
  const {data} = await apiClient.get<GetReProductLocationViewResponse>(
    productionEndpoint('/view/reproductionlocationview'),
    {params}
  );
  return data;
};

export const saveReProductionLocation = async (
  body: SaveReProductionLocationRequestBody
): Promise<SaveReProductionLocationResponse> => {
  try {
    const {data} = await apiClient.post<SaveReProductionLocationResponse>(
      productionEndpoint('/reproduction/location'),
      body
    );
    return data;
  } catch (error) {
    throw parseApiErrorWithLocalization(
      error as AxiosError<BackendErrorData>,
      'ReProductions'
    );
  }
};

export const getReProductionConfirmView = async (
  params: GetConfirmViewRequestParam
): Promise<GetReProductionConfirmViewResponse> => {
  const {data} = await apiClient.get<GetReProductionConfirmViewResponse>(
    productionEndpoint('/reproduction/confirm-view'),
    {
      params: {
        productionId: params.productionId,
        securityGroupId: params.securityGroupId,
        ModuleType: params.ModuleType,
      },
    }
  );
  return data;
};

export const confirmReProduction = async (
  params: ConfirmProductionRequestParams
): Promise<ConfirmProductionResponse> => {
  try {
    const {data} = await apiClient.request<ConfirmProductionResponse>({
      method: 'GET',
      url: productionEndpoint('/reproduction/confirm'),
      params: {
        ProductionId: params.ProductionId,
        SecurityGroupId: params.SecurityGroupId,
        ProductionLabel: params.ProductionLabel,
        IsCommit: params.IsCommit,
      },
    });
    return data;
  } catch (error) {
    throw parseApiErrorWithLocalization(
      error as AxiosError<BackendErrorData>,
      'ReProductions'
    );
  }
};

export const getReProductionComponents = async (
  params: GetProductionComponentsRequestParam
): Promise<GetProductionComponentsResponse> => {
  const {data} = await apiClient.get(
    productionEndpoint('/view/reproduction/productioncomponents'),
    {params}
  );
  return mapProductionComponentsRedactionStyles(data);
};

export const saveReProductionComponents = async (
  payload: SaveProductionComponentsPayload
): Promise<SaveProductionComponentsResponse> => {
  const {data} = await apiClient.post<SaveProductionComponentsResponse>(
    productionEndpoint('/reproduction/components'),
    payload
  );
  return data;
};
export const getDatComponentReproduction = async (
  params: GetDatComponentRequestParam
): Promise<GetDatComponentResponse> => {
  const {data} = await apiClient.get<GetDatComponentResponse>(
    productionEndpoint('/view/reproduction/datcomponent'),
    {params}
  );
  return data;
};

export const getNativeComponentReproduction = async (
  params: GetNativeComponentRequestParam
): Promise<GetNativeComponentResponse> => {
  const {data} = await apiClient.get<GetNativeComponentResponse>(
    productionEndpoint('/view/reproduction/nativecomponent'),
    {params}
  );
  return data;
};

export const getTextComponentReproduction = async (
  params: GetTextComponentRequestParam
): Promise<GetTextComponentResponse> => {
  const {data} = await apiClient.get<GetTextComponentResponse>(
    productionEndpoint('/view/reproduction/textcomponent'),
    {params}
  );
  return data;
};

export const getTiffComponentReproduction = async (
  params: GetTiffComponentRequestParam
): Promise<GetTiffComponentResponse> => {
  const {data} = await apiClient.get<GetTiffComponentResponse>(
    productionEndpoint('/view/reproduction/tiffcomponent'),
    {params}
  );
  return data;
};
